import React, { useCallback, useMemo, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  ApiKeyAddForm,
  ApiKeyAddInitialValues,
  ApiKeyComponents,
  ApiKeyAdditionalParams,
  ApiKeyFormOptions,
  onApiKeyFormSaved,
  IApiKeyFormOptionsProps,
  AlertError,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateApiKey, PermissionEnum, } from 'atlas-shared';
import { createApiKey, getApiKeyCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useUsers, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateApiKey> {}

const ApiKeyFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const users = useUsers();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const param_props: IApiKeyFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    users: users.users,
  };
  const formLayout = useMemo(() => ApiKeyAddForm(values, param_props, t), [values, organizations, users, t, abilities, ]);
  const options: IFormOptions = useMemo(() => ApiKeyFormOptions(values || ApiKeyAddInitialValues, param_props, t), [values, organizations, users, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateApiKey, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateApiKey) => save(() => createApiKey(values).then(
    res => onSaved ? onSaved(res, navigate) : onApiKeyFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !users.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.ApiKey, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={ApiKeyComponents}
        additionalParams={ApiKeyAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...ApiKeyAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('API_KEY')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const ApiKeyFormAddView = React.memo(withAdd(ApiKeyFormAdd, getApiKeyCreateSchema, ApiKeyAddInitialValues));
